import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Archive, RefreshCw, Search, GripVertical, Trash2, AlertTriangle, HardDrive, ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { dbBackupApi } from '../../services/api';
import { formatTimestamp } from '../../utils/dateTime';

/**
 * Administration > Database > Backup Browser
 *
 * Read-only view of the db-exports container (DevTrack 125). Deliberately NO download:
 * a bacpac is the entire database in one file with no row-level permissions and no
 * redaction, so retrieval happens through the Azure Portal / Storage Explorer by
 * whoever already holds Azure access.
 *
 * Blobs are stored date-first ({yyyy-MM-dd}/{db}_{HHmm}Z.bacpac), so one night's run is
 * one folder. This page mirrors that: collapsible folders, expand to see the files —
 * same group/expand shape as AuditTrailAdminPage, so the two read alike.
 *
 * The "n of m" completeness count uses the number of ENABLED SCHEDULES as its
 * denominator, not "the busiest folder we happen to have seen". It is only applied to
 * the most recent folder: older nights legitimately have fewer files if fewer databases
 * were scheduled back then, and retroactively judging them against today's schedule
 * would flag history that was never wrong.
 *
 * SysAdmin only (route-guarded in App.js, nav flagged sysAdminOnly).
 * STUI-only: no new classes, no inline styles.
 */
const UNDATED = '(no date folder)';

const BackupBrowserPage = () => {
  const [blobs, setBlobs] = useState([]);
  const [totalBytes, setTotalBytes] = useState(0);
  const [expectedPerNight, setExpectedPerNight] = useState(0);
  const [scheduledDbs, setScheduledDbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(null);
    try {
      // Jobs come along for the ride so the completeness count has a real denominator.
      const [blobRes, jobRes] = await Promise.allSettled([
        dbBackupApi.getBlobs({}),
        dbBackupApi.getJobs(),
      ]);

      if (blobRes.status === 'rejected') throw blobRes.reason;

      const list = blobRes.value.data?.blobs || [];
      setBlobs(list);
      setTotalBytes(blobRes.value.data?.totalBytes || 0);

      if (jobRes.status === 'fulfilled') {
        const enabled = (jobRes.value.data || []).filter(t => t.job && t.job.isEnabled);
        setExpectedPerNight(enabled.length);
        setScheduledDbs(enabled.map(t => t.databaseName));
      } else {
        setExpectedPerNight(0);
        setScheduledDbs([]);
      }

      // Open the newest folder by default — "did last night work?" is the question this
      // page exists to answer, and it should be answerable without a click.
      const newest = list
        .map(b => b.dateFolder)
        .filter(Boolean)
        .sort()
        .pop();
      if (newest) setExpanded(prev => (Object.keys(prev).length ? prev : { [newest]: true }));
    } catch (err) {
      if (err.response?.status === 503) {
        setUnavailable(err.response?.data?.error || 'Blob storage is not configured in this environment.');
      } else {
        notify.error(err.response?.data?.error || 'Failed to load backups');
      }
      setBlobs([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtSize = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
    return `${(bytes / 1073741824).toFixed(2)} GB`;
  };

  const fmtModified = (iso) => formatTimestamp(iso, { showZone: true }) || '—';

  const handleDelete = (path) => {
    notify.confirm(
      `Permanently delete '${path}'? Long-term retention is off, so this file may be the only copy of that database from that date.`,
      async () => {
        try {
          await dbBackupApi.deleteBlob(path);
          await load();
          notify.success('Backup deleted');
        } catch (err) {
          notify.error(err.response?.data?.error || 'Delete failed');
        }
      }
    );
  };

  const toggleFolder = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const setAll = (open) => {
    if (!open) { setExpanded({}); return; }
    const all = {};
    groupKeys.forEach(k => { all[k] = true; });
    setExpanded(all);
  };

  const filtered = searchTerm.trim()
    ? blobs.filter(b => b.path.toLowerCase().includes(searchTerm.trim().toLowerCase()))
    : blobs;

  // Group by date folder, newest first; undated legacy blobs sink to the bottom.
  const groups = filtered.reduce((acc, b) => {
    const key = b.dateFolder || UNDATED;
    (acc[key] = acc[key] || []).push(b);
    return acc;
  }, {});
  const datedKeys = Object.keys(groups).filter(k => k !== UNDATED).sort().reverse();
  const groupKeys = [...datedKeys, ...(groups[UNDATED] ? [UNDATED] : [])];
  const newestDated = datedKeys[0];

  const missingFrom = (rows) => {
    if (!scheduledDbs.length) return [];
    const present = new Set(rows.map(r => r.databaseName));
    return scheduledDbs.filter(d => !present.has(d));
  };

  if (loading) return <div className="stui-detail-empty"><Archive size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Archive size={20} />
          <h1>Backup Browser</h1>
          <span className="stui-module-header-meta">
            db-exports · {groupKeys.length} folder{groupKeys.length === 1 ? '' : 's'} · {blobs.length} files · {fmtSize(totalBytes)}
          </span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-search-wrapper">
            <Search size={14} className="stui-search-icon" />
            <input
              className="stui-field-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search backups..."
            />
          </div>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setAll(true); }}>
                  <FolderOpen size={16} /><span>Expand All</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setAll(false); }}>
                  <Folder size={16} /><span>Collapse All</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {unavailable ? (
        <div className="stui-detail-empty">
          <HardDrive size={48} />
          <p>{unavailable}</p>
        </div>
      ) : groupKeys.length === 0 ? (
        <div className="stui-detail-empty">
          <Archive size={48} />
          <p>
            {searchTerm
              ? 'No backups match that search.'
              : 'No backups yet. Schedule one on the Database Backups page, or use Run Now.'}
          </p>
        </div>
      ) : (
        <div className="stui-datasheet-view">
          <div className="stui-data-grid-container">
            <div className="stui-data-grid-scroll-area">
              <table className="stui-data-grid">
                <thead>
                  <tr>
                    {/* stui-col-delete is the established 40px column width — without it
                        the chevron column stretches to share the table evenly. */}
                    <th className="stui-col-delete"></th>
                    <th>Folder</th>
                    <th>Contents</th>
                    <th className="stui-cell-right">Size</th>
                    <th>Latest</th>
                  </tr>
                </thead>
                <tbody>
                  {groupKeys.map(key => {
                    const rows = groups[key].slice().sort((a, b) => a.databaseName.localeCompare(b.databaseName));
                    const isOpen = !!expanded[key];
                    const undated = key === UNDATED;
                    const groupBytes = rows.reduce((sum, r) => sum + (r.sizeBytes || 0), 0);
                    const latest = rows
                      .map(r => r.modified)
                      .filter(Boolean)
                      .sort()
                      .pop();

                    // Only the newest dated folder gets judged for completeness.
                    const judged = !undated && key === newestDated && expectedPerNight > 0;
                    const missing = judged ? missingFrom(rows) : [];
                    const short = judged && missing.length > 0;
                    // Count DATABASES, not files: a manual re-run puts two files in the
                    // same folder for one database, and "2 of 6" would then imply two
                    // databases were covered when only one was.
                    const distinctDbs = new Set(rows.map(r => r.databaseName)).size;

                    return (
                      <React.Fragment key={key}>
                        <tr className="stui-clickable" onClick={() => toggleFolder(key)}>
                          <td className="stui-cell-center">
                            <span className="stui-td-muted">{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
                          </td>
                          <td className="stui-text-nowrap stui-fw-semibold">
                            {isOpen ? <FolderOpen size={14} /> : <Folder size={14} />} {key}
                          </td>
                          <td>
                            {judged
                              ? <>
                                  <span className={short ? 'stui-status-failed' : 'stui-status-complete'}>
                                    {distinctDbs} of {expectedPerNight} databases
                                  </span>
                                  <span className="stui-td-muted"> · {rows.length} file{rows.length === 1 ? '' : 's'}</span>
                                </>
                              : <span className="stui-td-muted">
                                  {distinctDbs} database{distinctDbs === 1 ? '' : 's'} · {rows.length} file{rows.length === 1 ? '' : 's'}
                                </span>}
                            {short && (
                              <span className="stui-status-failed">
                                {' '}<AlertTriangle size={12} /> missing {missing.join(', ')}
                              </span>
                            )}
                            {undated && (
                              <span className="stui-td-muted"> · not created by the scheduler, never auto-reaped</span>
                            )}
                          </td>
                          <td className="stui-cell-right stui-text-nowrap">{fmtSize(groupBytes)}</td>
                          <td className="stui-mono stui-text-nowrap">{fmtModified(latest)}</td>
                        </tr>

                        {isOpen && (
                          <tr>
                            <td colSpan={5}>
                              <table className="stui-inner-table">
                                <thead>
                                  <tr>
                                    <th>Database</th>
                                    <th className="stui-cell-right">Size</th>
                                    <th>Tier</th>
                                    <th>Modified</th>
                                    <th>Path</th>
                                    <th className="stui-inner-table-col-actions"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map(b => (
                                    <tr key={b.path}>
                                      <td className="stui-text-nowrap">{b.databaseName}</td>
                                      <td className="stui-cell-right stui-text-nowrap">{fmtSize(b.sizeBytes)}</td>
                                      <td className="stui-td-muted">{b.tier || '—'}</td>
                                      <td className="stui-inner-table-cell-mono stui-text-nowrap">{fmtModified(b.modified)}</td>
                                      <td className="stui-inner-table-cell-mono">{b.path}</td>
                                      <td>
                                        {/* Per-row actions rather than select-then-act: for a
                                            destructive action on a file that may be the only
                                            copy, the target has to be unambiguous. */}
                                        <div className="stui-inner-table-cell-actions">
                                          <button
                                            className="stui-entry-action-btn"
                                            onClick={() => handleDelete(b.path)}
                                            title={`Delete ${b.path}`}
                                          >
                                            <Trash2 size={12} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupBrowserPage;
